import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, Bot, CheckCircle2, LifeBuoy, Play, Plus, Send,
  ShieldCheck, Sparkles, X,
} from "lucide-react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { LoadingState } from "../components/LoadingState";
import api, { formatApiError } from "../lib/api";

const STARTERS = [
  "Explain my latest CRM health score and what I should fix first.",
  "Which findings can CleanIQ correct safely, and which need manual review?",
  "Help me understand my connected CRMs and scan history.",
  "What should I do before approving a CRM correction?",
];

function when(value) {
  return value ? new Date(value).toLocaleString() : "";
}

export default function Calia() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [connections, setConnections] = useState([]);
  const [scans, setScans] = useState([]);
  const [connectionId, setConnectionId] = useState("");
  const [scanId, setScanId] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [reportPreview, setReportPreview] = useState(null);
  const [reporting, setReporting] = useState(false);
  const [actionPreview, setActionPreview] = useState(null);
  const [actionRunning, setActionRunning] = useState(false);

  const loadConversation = useCallback(async (conversationId) => {
    if (!conversationId) { setActive(null); return; }
    const { data } = await api.get(`/calia/conversations/${conversationId}`);
    setActive(data);
  }, []);

  const load = useCallback(async () => {
    try {
      const [
        { data: statusData },
        { data: conversationData },
        { data: connectionData },
        { data: scanData },
      ] = await Promise.all([
        api.get("/calia/status"),
        api.get("/calia/conversations"),
        api.get("/connections"),
        api.get("/scans"),
      ]);
      setStatus(statusData);
      setConversations(conversationData.items || []);
      setConnections(connectionData || []);
      setScans(scanData || []);
      if ((conversationData.items || []).length) {
        await loadConversation(conversationData.items[0].id);
      }
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setLoading(false); }
  }, [loadConversation]);

  useEffect(() => { load(); }, [load]);

  const filteredScans = useMemo(
    () => scans.filter((scan) => !connectionId || scan.connection_id === connectionId),
    [scans, connectionId],
  );

  const send = async (event) => {
    event.preventDefault();
    if (!message.trim() || sending || !status?.available) return;
    const outgoing = message.trim();
    setMessage("");
    setSending(true);
    try {
      const { data } = await api.post("/calia/messages", {
        message: outgoing,
        conversation_id: active?.id || null,
        connection_id: connectionId || null,
        scan_id: scanId || null,
      });
      const conversation = data.conversation;
      setActive((current) => ({
        ...(current || conversation),
        ...conversation,
        messages: [
          ...(current?.messages || []),
          data.user_message,
          data.assistant_message,
        ],
        actions: [
          ...(current?.actions || []),
          ...(data.action ? [data.action] : []),
        ],
      }));
      setConversations((items) => [
        conversation,
        ...items.filter((item) => item.id !== conversation.id),
      ]);
    } catch (error) {
      setMessage(outgoing);
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setSending(false); }
  };

  const updateAction = (updated) => {
    setActive((current) => ({
      ...current,
      actions: (current?.actions || []).map((item) => (
        item.id === updated.id ? updated : item
      )),
    }));
  };

  const approveAction = async () => {
    if (!actionPreview?.id || actionRunning) return;
    setActionRunning(true);
    try {
      const { data } = await api.post(`/calia/actions/${actionPreview.id}/approve`, {
        confirmed: true,
      });
      updateAction(data);
      setActionPreview(null);
      if (data.action_type === "start_scan" && data.result?.scan_id) {
        toast.success("Scan queued after your approval.");
        navigate(`/scans/${data.result.scan_id}`);
      } else {
        toast.success(`${data.result?.applied || 0} correction(s) applied after your approval.`);
        await load();
      }
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setActionRunning(false); }
  };

  const rejectAction = async (action) => {
    if (!action?.id || actionRunning) return;
    setActionRunning(true);
    try {
      const { data } = await api.post(`/calia/actions/${action.id}/reject`);
      updateAction(data);
      toast.success("Action declined. Nothing was changed.");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setActionRunning(false); }
  };

  const prepareIssueReport = async () => {
    if (!active?.id) return;
    setReporting(true);
    try {
      const { data } = await api.get(
        `/calia/conversations/${active.id}/issue-report-preview`,
        { params: {
          connection_id: connectionId || undefined,
          scan_id: scanId || undefined,
        } },
      );
      setReportPreview(data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setReporting(false); }
  };

  const confirmIssueReport = async () => {
    if (!active?.id || !reportPreview) return;
    setReporting(true);
    try {
      const { data } = await api.post(
        `/calia/conversations/${active.id}/issue-report`,
        {
          confirmed: true,
          connection_id: connectionId || null,
          scan_id: scanId || null,
        },
      );
      toast.success(`Issue reported as ${data.reference}.`);
      navigate(`/support?ticket=${encodeURIComponent(data.id)}`);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setReporting(false); }
  };

  if (loading) return <AppShell><LoadingState label="Loading Calia" /></AppShell>;

  return <AppShell>
    <div className="space-y-5" data-testid="calia-page">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-[#081426] text-white shadow-xl">
        <div className="grid gap-8 p-6 lg:grid-cols-[1fr_auto] lg:p-8">
          <div>
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-amber-300">
              <Sparkles className="h-4 w-4" /> Contextual CRM assistant
            </div>
            <h1 className="mt-3 font-heading text-3xl font-bold">Ask Calia about your CleanIQ workspace</h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">
              Calia can interpret your credential-free account context, scans, findings, and support history. She cannot expose credentials or bypass approval gates.
            </p>
            <p className="mt-2 max-w-3xl text-xs leading-5 text-slate-400">
              When enabled, your message and the displayed credential-free context are processed by OpenAI to generate the response.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs">
            <ShieldCheck className="h-4 w-4 text-emerald-300" />
            {status?.available ? "AI service ready" : "Awaiting secure AI configuration"}
          </div>
        </div>
      </div>

      {!status?.available && <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-950">
        Calia is built but unavailable until the secure OpenAI key and feature switch are configured in production. CleanIQ will not substitute canned chatbot replies.
      </div>}

      <div className="grid gap-5 xl:grid-cols-[300px_1fr]">
        <aside className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-4">
            <button onClick={() => { setActive(null); setMessage(""); }}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white">
              <Plus className="h-4 w-4" /> New conversation
            </button>
          </div>
          <div className="max-h-[620px] divide-y divide-slate-100 overflow-auto">
            {conversations.map((conversation) => <button key={conversation.id}
              onClick={() => loadConversation(conversation.id)}
              className={"w-full p-4 text-left hover:bg-slate-50 " + (active?.id === conversation.id ? "bg-blue-50" : "")}>
              <div className="line-clamp-2 text-sm font-semibold text-slate-900">{conversation.title}</div>
              <div className="mt-2 text-[10px] text-slate-400">{when(conversation.updated_at)}</div>
            </button>)}
            {!conversations.length && <div className="p-8 text-center text-xs leading-5 text-slate-500">Your private Calia conversations will appear here.</div>}
          </div>
        </aside>

        <section className="flex min-h-[650px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="grid gap-3 border-b border-slate-200 p-4 sm:grid-cols-2">
            <label className="text-xs font-semibold text-slate-600">Focus CRM
              <select value={connectionId} onChange={(e) => { setConnectionId(e.target.value); setScanId(""); }}
                className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
                <option value="">All accessible CRMs</option>
                {connections.map((item) => <option key={item.id} value={item.id}>{item.label} · {item.connector_name}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-600">Focus scan
              <select value={scanId} onChange={(e) => setScanId(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
                <option value="">Recent scan context</option>
                {filteredScans.slice(0, 30).map((item) => <option key={item.id} value={item.id}>{item.id} · {item.status}</option>)}
              </select>
            </label>
          </div>

          <div className="flex-1 space-y-4 overflow-auto bg-slate-50 p-5">
            {!active?.messages?.length && <div className="mx-auto max-w-3xl py-12 text-center">
              <Bot className="mx-auto h-12 w-12 text-blue-500" />
              <h2 className="mt-4 font-heading text-2xl font-semibold">What would you like to understand?</h2>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {STARTERS.map((starter) => <button key={starter} onClick={() => setMessage(starter)}
                  className="rounded-xl border border-slate-200 bg-white p-4 text-left text-sm leading-6 text-slate-700 hover:border-blue-300 hover:bg-blue-50">
                  {starter}
                </button>)}
              </div>
            </div>}
            {(active?.messages || []).map((item) => <article key={item.id}
              className={"max-w-3xl rounded-2xl border p-4 " + (item.role === "assistant" ? "border-blue-200 bg-blue-50" : "ml-auto border-slate-200 bg-white")}>
              <div className="text-xs font-semibold text-slate-700">{item.role === "assistant" ? "Calia" : "You"}</div>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">{item.body}</p>
              <div className="mt-2 text-[10px] text-slate-400">{when(item.created_at)}</div>
            </article>)}
            {(active?.actions || []).map((action) => <article key={action.id}
              data-testid={`calia-action-${action.id}`}
              className="max-w-3xl rounded-2xl border border-amber-300 bg-amber-50 p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-800">
                    <ShieldCheck className="h-4 w-4" /> Approval-gated action
                  </div>
                  <h3 className="mt-2 font-semibold text-slate-950">{action.title}</h3>
                  <p className="mt-1 text-sm leading-6 text-slate-700">{action.summary}</p>
                </div>
                <span className={"rounded-full px-2.5 py-1 text-xs font-semibold " + (
                  action.status === "completed"
                    ? "bg-emerald-100 text-emerald-800"
                    : action.status === "pending_approval"
                      ? "bg-amber-200 text-amber-900"
                      : "bg-slate-200 text-slate-700"
                )}>
                  {action.status.replaceAll("_", " ")}
                </span>
              </div>
              {action.status === "pending_approval" && <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" onClick={() => rejectAction(action)} disabled={actionRunning}
                  data-testid={`calia-reject-${action.id}`}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700">
                  <X className="h-4 w-4" /> Decline
                </button>
                <button type="button" onClick={() => setActionPreview(action)} disabled={actionRunning}
                  data-testid={`calia-review-${action.id}`}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-amber-600 px-4 py-2 text-xs font-semibold text-white">
                  <ShieldCheck className="h-4 w-4" /> Review exact action
                </button>
              </div>}
              {action.status === "completed" && <div className="mt-3 flex items-center gap-2 text-xs font-medium text-emerald-800">
                <CheckCircle2 className="h-4 w-4" />
                Completed only after your approval{action.result?.rescan_required ? " · run a new scan to refresh the score" : ""}
              </div>}
              {action.status === "failed" && <div className="mt-3 text-xs font-medium text-red-700">
                Action failed safely: {action.error}
              </div>}
            </article>)}
            {sending && <div className="max-w-3xl rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700">Calia is reviewing your CleanIQ context…</div>}
          </div>

          <div className="border-t border-slate-200 p-4">
            {active?.messages?.length > 0 && <button onClick={prepareIssueReport}
              disabled={reporting}
              className="mb-3 inline-flex items-center gap-2 text-xs font-semibold text-blue-700 hover:text-blue-900">
              <LifeBuoy className="h-4 w-4" /> {reporting ? "Preparing report…" : "Report issue to CleanIQ Support"}
            </button>}
            <form onSubmit={send} className="flex gap-3">
              <textarea data-testid="calia-message" required minLength={2} maxLength={4000} rows={3}
                value={message} onChange={(e) => setMessage(e.target.value)}
                disabled={!status?.available || sending}
                placeholder={status?.available ? "Ask about scans, findings, corrections, navigation, or next steps…" : "Calia is awaiting secure configuration."}
                className="min-w-0 flex-1 rounded-xl border border-slate-300 px-4 py-3 text-sm disabled:bg-slate-100" />
              <button data-testid="calia-send" disabled={!status?.available || sending || !message.trim()}
                className="inline-flex items-center gap-2 self-end rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white disabled:opacity-40">
                <Send className="h-4 w-4" /> Send
              </button>
            </form>
            <p className="mt-2 text-[11px] text-slate-400">Do not enter passwords, API keys, authentication codes, or payment information.</p>
          </div>
        </section>
      </div>

      {reportPreview && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
        role="dialog" aria-modal="true" aria-labelledby="calia-report-title">
        <div className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-blue-600">Review before sending</div>
              <h2 id="calia-report-title" className="mt-2 font-heading text-2xl font-bold text-slate-950">Report this issue to CleanIQ Support?</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Nothing is sent until you confirm. The ticket will be linked to your account and the selected CRM or scan.
              </p>
            </div>
            <ShieldCheck className="h-7 w-7 shrink-0 text-emerald-600" />
          </div>

          <dl className="mt-5 grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm sm:grid-cols-2">
            <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Subject</dt><dd className="mt-1 font-medium text-slate-900">{reportPreview.subject}</dd></div>
            <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Category</dt><dd className="mt-1 font-medium capitalize text-slate-900">{reportPreview.category.replace("_", " ")}</dd></div>
            <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Organization</dt><dd className="mt-1 font-medium text-slate-900">{reportPreview.diagnostic_snapshot?.account?.organization_name || reportPreview.diagnostic_snapshot?.organization_id}</dd></div>
            <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">CRM</dt><dd className="mt-1 font-medium text-slate-900">{reportPreview.diagnostic_snapshot?.connection?.label || "No CRM selected"}</dd></div>
            <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Scan</dt><dd className="mt-1 font-medium text-slate-900">{reportPreview.diagnostic_snapshot?.scan?.id || "No scan selected"}</dd></div>
            <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Conversation</dt><dd className="mt-1 break-all font-mono text-xs text-slate-900">{reportPreview.diagnostic_snapshot?.calia_conversation_id}</dd></div>
          </dl>

          <div className="mt-4">
            <h3 className="text-sm font-semibold text-slate-900">Message being sent</h3>
            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-2xl border border-slate-200 bg-white p-4 font-sans text-sm leading-6 text-slate-700">{reportPreview.description}</pre>
          </div>

          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-sm font-semibold text-emerald-950">Excluded from this report</div>
            <ul className="mt-2 grid gap-1 text-xs text-emerald-900 sm:grid-cols-2">
              {(reportPreview.excludes || []).map((item) => <li key={item}>• {item}</li>)}
            </ul>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button onClick={() => setReportPreview(null)} disabled={reporting}
              className="rounded-xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700">
              Cancel
            </button>
            <button data-testid="calia-confirm-report" onClick={confirmIssueReport} disabled={reporting}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50">
              <LifeBuoy className="h-4 w-4" /> {reporting ? "Reporting…" : "Confirm and report issue"}
            </button>
          </div>
        </div>
      </div>}

      {actionPreview && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
        role="dialog" aria-modal="true" aria-labelledby="calia-action-title">
        <div className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-3xl border border-amber-200 bg-white p-6 shadow-2xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-1 h-6 w-6 shrink-0 text-amber-600" />
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-amber-700">Nothing runs until you approve</div>
              <h2 id="calia-action-title" className="mt-2 font-heading text-2xl font-bold text-slate-950">{actionPreview.title}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">{actionPreview.summary}</p>
            </div>
          </div>

          <dl className="mt-5 grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm sm:grid-cols-2">
            <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">CRM</dt><dd className="mt-1 font-medium text-slate-900">{actionPreview.impact?.crm}</dd></div>
            <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Provider</dt><dd className="mt-1 font-medium text-slate-900">{actionPreview.impact?.provider}</dd></div>
            {actionPreview.impact?.scan_id && <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Scan</dt><dd className="mt-1 break-all font-mono text-xs text-slate-900">{actionPreview.impact.scan_id}</dd></div>}
            {actionPreview.impact?.corrections != null && <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Exact corrections</dt><dd className="mt-1 font-medium text-slate-900">{actionPreview.impact.corrections} across {actionPreview.impact.contacts} contact(s)</dd></div>}
            {actionPreview.impact?.fields?.length > 0 && <div className="sm:col-span-2"><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fields</dt><dd className="mt-1 font-medium text-slate-900">{actionPreview.impact.fields.join(", ")}</dd></div>}
          </dl>

          {actionPreview.impact?.preview?.length > 0 && <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
            <div className="bg-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Change preview</div>
            <div className="max-h-64 divide-y divide-slate-100 overflow-auto">
              {actionPreview.impact.preview.map((item, index) => <div key={`${item.contact_name}-${item.field}-${index}`} className="grid gap-1 px-4 py-3 text-sm sm:grid-cols-[1fr_120px_1fr]">
                <div className="font-medium text-slate-900">{item.contact_name}</div>
                <div className="text-slate-500">{item.field}</div>
                <div className="font-mono text-xs text-slate-700">{String(item.current || "—")} → {String(item.new_value)}</div>
              </div>)}
            </div>
            {actionPreview.impact.corrections > actionPreview.impact.preview.length && <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-500">
              Showing 10 of {actionPreview.impact.corrections} exact corrections. All corrections remain bound to this completed scan.
            </div>}
          </div>}

          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950">
            Calia cannot alter this payload after it is displayed. CleanIQ rechecks your account, plan, CRM ownership, scan state, and live record values during execution.
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button onClick={() => setActionPreview(null)} disabled={actionRunning}
              className="rounded-xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700">
              Cancel
            </button>
            <button data-testid="calia-confirm-action" onClick={approveAction} disabled={actionRunning}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50">
              <Play className="h-4 w-4" /> {actionRunning ? "Executing…" : "Approve and execute"}
            </button>
          </div>
        </div>
      </div>}
    </div>
  </AppShell>;
}
